import { Grid } from "@material-ui/core";
import { Skeleton } from "@material-ui/lab";
import { A } from "@mobily/ts-belt";
import PropTypes from "prop-types";
import * as React from "react";
import { useHistory, useLocation } from "react-router-dom";
import { InfluencerEvent } from "../../domain";
import { useInfluencerEvents } from "../../hooks";
import { useCurrentUser } from "../../globalState";
import { ErrorUI, SemiBoldText, StyledTextButton } from "../../theme";
import AllEventsModal from "./AllEventsModal";
import EventHeader from "./EventHeader";
import InfluencerEventElement from "./InfluencerEventElement";
import { dashboardPath } from "../../domain/dashboardPaths";

export const loadingUI = (
  <Grid container>
    <Grid item>
      <Skeleton width={300} height={16} />
    </Grid>

    <Grid item>
      <Skeleton width={300} height={16} />
    </Grid>

    <Grid item>
      <Skeleton width={300} height={16} />
    </Grid>
  </Grid>
);

// Partition events into live-first, then non-live. Keep original relative
// order within each group so live events (the primary CTA) surface first and
// non-live batch orders are visually de-prioritized beneath them.
function partitionByLiveness(events) {
  const live = [];
  const nonLive = [];
  for (const evt of events) {
    if (InfluencerEvent.isNonLiveSigning(evt)) {
      nonLive.push(evt);
    } else {
      live.push(evt);
    }
  }
  return { live, nonLive };
}

function splitUpEvents(events) {
  const ordered = (() => {
    const { live, nonLive } = partitionByLiveness(events);
    return [...live, ...nonLive];
  })();

  const firstEvents = ordered.slice(0, 5);
  const allEvents = ordered;

  return { firstEvents, allEvents };
}

const UpcomingSignings = React.memo(function UpcomingSignings({
  influencerId,
  alignItems = "flex-start",
  allowEdit = false,
  isGroupShop = false,
}) {
  const currentUser = useCurrentUser();
  const history = useHistory();
  const location = useLocation();
  const [showAllEventsModal, setShowAllEventsModal] = React.useState(false);

  const { influencerEvents, isLoading, isError, refresh } =
    useInfluencerEvents(influencerId);

  // Detect return from EventFormPage
  React.useEffect(() => {
    if (location.state?.eventUpdated) {
      refresh();
      history.replace(location.pathname, {});
    }
  }, [location.state?.eventUpdated, refresh, history, location.pathname]);

  const { firstEvents, allEvents } = React.useMemo(() => {
    if (!Array.isArray(influencerEvents)) {
      return { firstEvents: [], allEvents: [] };
    }

    return splitUpEvents(influencerEvents);
  }, [influencerEvents]);

  const { liveFirstEvents, nonLiveFirstEvents } = React.useMemo(
    () => ({
      liveFirstEvents: firstEvents.filter(
        (evt) => !InfluencerEvent.isNonLiveSigning(evt),
      ),
      nonLiveFirstEvents: firstEvents.filter((evt) =>
        InfluencerEvent.isNonLiveSigning(evt),
      ),
    }),
    [firstEvents],
  );

  if (isLoading) {
    return loadingUI;
  }

  if (isError) {
    return <ErrorUI message="Unable to load upcoming events" />;
  }

  const align = alignItems === "flex-end" ? "right" : "left";
  const headerAlign = alignItems === "flex-end" ? "right" : "left";

  const makeOnClick = (evt) => {
    if (isGroupShop) {
      return () => {
        history.push(`/${evt.influencerRoute}`);
      };
    }

    if (
      allowEdit &&
      !currentUser.isAdmin &&
      InfluencerEvent.isNonLiveSigning(evt)
    ) {
      return undefined;
    }

    if (allowEdit) {
      return () => {
        const raw = evt.raw();
        history.push(
          dashboardPath({
            shop: evt.influencerRoute,
            page: `events/${raw.influencer_event_id}/edit`,
          }),
          {
            returnTo: location.pathname,
          },
        );
      };
    }
  };

  return (
    <Grid container direction="column" alignItems={alignItems} spacing={1}>
      {!firstEvents.length && (
        <Grid item>
          <EventHeader align={headerAlign} />

          <SemiBoldText variant="body2" align={align}>
            No upcoming events.
          </SemiBoldText>
        </Grid>
      )}

      {A.isNotEmpty(liveFirstEvents) && (
        <Grid item>
          <EventHeader align={headerAlign} />
          {liveFirstEvents.map((influencerEvent) => (
            <div key={influencerEvent.influencerEventId}>
              <InfluencerEventElement
                align={align}
                influencerEvent={influencerEvent}
                onClick={makeOnClick(influencerEvent)}
                variant="live"
              />
            </div>
          ))}
        </Grid>
      )}

      {A.isNotEmpty(nonLiveFirstEvents) && (
        <Grid item>
          <EventHeader
            label="Off-Camera Fulfillment Schedule"
            align={headerAlign}
          />
          {nonLiveFirstEvents.map((influencerEvent) => (
            <div key={influencerEvent.influencerEventId}>
              <InfluencerEventElement
                align={align}
                influencerEvent={influencerEvent}
                onClick={makeOnClick(influencerEvent)}
                variant="nonLive"
              />
            </div>
          ))}
        </Grid>
      )}

      {firstEvents.length < allEvents.length && (
        <StyledTextButton
          onClick={() => {
            setShowAllEventsModal(true);
          }}
          aria-label={`See ${
            allEvents.length - firstEvents.length
          } more events`}
        >
          See {allEvents.length - firstEvents.length} more events
        </StyledTextButton>
      )}

      {showAllEventsModal && (
        <AllEventsModal
          influencerEvents={allEvents}
          open={showAllEventsModal}
          onClose={() => {
            setShowAllEventsModal(false);
          }}
        />
      )}
    </Grid>
  );
});

UpcomingSignings.propTypes = {
  influencerId: PropTypes.number.isRequired,
  alignItems: PropTypes.oneOf(["flex-start", "flex-end"]),
  allowEdit: PropTypes.bool,
  isGroupShop: PropTypes.bool,
};

export default UpcomingSignings;
